import React from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { Medcall2MessageForm } from './Medcall2MessageForm';

const MEDCALL_PARTICIPANT_SEND_MESSAGE_DRAFT = gql`
  query MedcallParticipantSendMessageDraft($participantId: Int!, $preserveLegacySemantics: Boolean) {
    medcallParticipantSendMessageDraft(
      participantId: $participantId
      preserveLegacySemantics: $preserveLegacySemantics
    ) {
      ccRecipients
      providerRecipientCode
      providerRecipientFound
      providerRecipientLabel
      subject
      toRecipients
    }
  }
`;

const MEDCALL_SEND_PARTICIPANT_MESSAGE = gql`
  mutation MedcallSendParticipantMessage(
    $body: String
    $ccRecipients: [String!]
    $notes: String
    $participantId: Int!
    $preserveLegacySemantics: Boolean
    $toRecipients: [String!]!
    $urgent: Boolean
  ) {
    medcallSendParticipantMessage(
      body: $body
      ccRecipients: $ccRecipients
      notes: $notes
      participantId: $participantId
      preserveLegacySemantics: $preserveLegacySemantics
      toRecipients: $toRecipients
      urgent: $urgent
    ) {
      extra
      message
      responseCode
      responseID
    }
  }
`;

const NURSELESS_PROGRAMS = ['ODRH Scattered/ERC', 'FIST ERC', 'DSH ERC'];
const EMAIL_ADDRESS_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LEGACY_EMAIL_ADDRESS_REGEX = /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;

type MedcallPerson = {
  email?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  title?: string | null;
};

type MedcallParticipantMessagePreview = {
  active?: boolean | null;
  cid?: string | null;
  created_at?: string | null;
  dob?: string | null;
  facility?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  nurse_name?: MedcallPerson | null;
  participant_id?: number | null;
  program?: string | null;
  provider?: string | null;
  psychiatrist?: MedcallPerson | null;
  staff_assigned?: string | null;
};

type MedcallSendMessageDraft = {
  ccRecipients?: string[] | null;
  providerRecipientCode?: string | null;
  providerRecipientFound?: boolean | null;
  providerRecipientLabel?: string | null;
  subject?: string | null;
  toRecipients?: string[] | null;
};

type MedcallSendMessageDraftData = {
  medcallParticipantSendMessageDraft: MedcallSendMessageDraft | null;
};

type MedcallSendMessageResult = {
  extra?: string | null;
  message?: string | null;
  responseCode?: number | null;
  responseID?: number | null;
};

type MedcallSendParticipantMessageData = {
  medcallSendParticipantMessage: MedcallSendMessageResult | null;
};

type LegacySendState = 'idle' | 'sending' | 'sent';

const normalizeValue = (value: unknown) => `${value || ''}`.trim();

const joinRecipients = (values: string[] | null | undefined) => {
  if (!Array.isArray(values) || values.length < 1) {
    return '';
  }

  return values.join(', ');
};

const formatDate = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const match = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return normalizedValue || '—';
  }

  return `${match[2]}/${match[3]}/${match[1]}`;
};

const formatDateTime = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const parsed = new Date(normalizedValue);
  if (Number.isNaN(parsed.getTime())) {
    return normalizedValue || '—';
  }

  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    month: '2-digit',
    timeZone: 'America/Los_Angeles',
    year: '2-digit',
  }).format(parsed).replace(',', '');
};

const formatPersonLabel = (person: MedcallPerson | null | undefined, program?: string | null) => {
  if (program && NURSELESS_PROGRAMS.includes(program)) {
    return 'N/A';
  }

  if (!person) {
    return '—';
  }

  const name = [person.firstname, person.lastname].filter(Boolean).join(' ').trim();
  const title = normalizeValue(person.title);
  if (!name && !title) {
    return '—';
  }

  if (!title) {
    return name || '—';
  }

  if (!name) {
    return title;
  }

  return `${name}, ${title}`;
};

const validateRecipientText = (value: string, fieldLabel: string, preserveCase = false) => {
  const entries = value
    .split(/[\n,;]+/)
    .map((entry) => (
      preserveCase ? normalizeValue(entry) : normalizeValue(entry).toLowerCase()
    ))
    .filter(Boolean);

  const uniqueRecipients: string[] = [];
  const invalidRecipients: string[] = [];
  const emailAddressRegex = preserveCase ? LEGACY_EMAIL_ADDRESS_REGEX : EMAIL_ADDRESS_REGEX;

  entries.forEach((entry) => {
    if (!emailAddressRegex.test(entry)) {
      invalidRecipients.push(entry);
      return;
    }

    if (uniqueRecipients.includes(entry)) {
      return;
    }

    uniqueRecipients.push(entry);
  });

  if (invalidRecipients.length > 0) {
    throw new Error(`Enter valid ${fieldLabel} email addresses.`);
  }

  return uniqueRecipients;
};

export const MedcallSendMessageDialog = ({
  onOpenChange,
  open,
  participant,
  participantId,
  preserveLegacySemantics = false,
  routeBase,
}: {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  participant: MedcallParticipantMessagePreview;
  participantId: number;
  preserveLegacySemantics?: boolean;
  routeBase?: string;
}) => {
  const isPromotedPage = routeBase === '/tools/medcall' || routeBase === '/tools/medcall2';
  const {
    data,
    error,
    loading,
  } = useQuery<MedcallSendMessageDraftData>(MEDCALL_PARTICIPANT_SEND_MESSAGE_DRAFT, {
    fetchPolicy: 'network-only',
    skip: !open || participantId < 1,
    variables: {
      participantId,
      preserveLegacySemantics,
    },
  });

  const [sendMessage, { loading: isSending }] = useMutation<MedcallSendParticipantMessageData>(MEDCALL_SEND_PARTICIPANT_MESSAGE);
  const [toRecipientsText, setToRecipientsText] = React.useState('');
  const [ccRecipientsText, setCcRecipientsText] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [urgent, setUrgent] = React.useState(false);
  const [draftSeed, setDraftSeed] = React.useState('');
  const [legacySendState, setLegacySendState] = React.useState<LegacySendState>('idle');
  const [legacyToRequired, setLegacyToRequired] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const sendInFlight = React.useRef(false);
  const sendGeneration = React.useRef(0);

  React.useEffect(() => {
    sendGeneration.current += 1;
    return () => { sendGeneration.current += 1; };
  }, [open, participantId]);

  const draft = data?.medcallParticipantSendMessageDraft;
  const defaultsUnavailable = Boolean(error) || !draft;
  const defaultsError = isPromotedPage && !loading && defaultsUnavailable ? 'Unable to load message defaults.' : undefined;
  const fieldsPending = isPromotedPage && submitting;
  const sendPending = isSending || fieldsPending;
  const subject = urgent
    ? `${normalizeValue(draft?.subject)} - URGENT`
    : normalizeValue(draft?.subject);

  React.useEffect(() => {
    if (!open) {
      setDraftSeed('');
      setToRecipientsText('');
      setCcRecipientsText('');
      setNotes('');
      setUrgent(false);
      setLegacySendState('idle');
      setLegacyToRequired(false);
      return;
    }

    if (!draft) {
      return;
    }

    const nextSeed = `${participantId}:${normalizeValue(draft.subject)}`;
    if (draftSeed === nextSeed) {
      return;
    }

    setDraftSeed(nextSeed);
    setToRecipientsText(joinRecipients(draft.toRecipients));
    setCcRecipientsText(joinRecipients(draft.ccRecipients));
    setNotes('');
    setUrgent(false);
  }, [draft, draftSeed, open, participantId]);

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (isPromotedPage && !nextOpen && sendInFlight.current) return;
    if (preserveLegacySemantics && legacySendState !== 'idle') return;
    if (!nextOpen) {
      setDraftSeed('');
      setToRecipientsText('');
      setCcRecipientsText('');
      setNotes('');
      setUrgent(false);
      setLegacySendState('idle');
    }

    onOpenChange(nextOpen);
  };

  const handleSendMessage = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isPromotedPage && (sendInFlight.current || loading || defaultsUnavailable)) return;

    let toRecipients: string[] = [];
    let ccRecipients: string[] = [];

    try {
      toRecipients = validateRecipientText(toRecipientsText, 'To', preserveLegacySemantics);
      ccRecipients = validateRecipientText(ccRecipientsText, 'CC', preserveLegacySemantics);
    } catch (validationError: any) {
      toast.error(validationError?.message || 'Enter valid email addresses.');
      return;
    }

    if (toRecipients.length < 1) {
      if (preserveLegacySemantics) {
        setLegacyToRequired(true);
        return;
      }
      toast.error('Enter at least one To recipient.');
      return;
    }

    const generation = sendGeneration.current;
    if (isPromotedPage) {
      sendInFlight.current = true;
      setSubmitting(true);
    }

    try {
      if (preserveLegacySemantics) {
        setLegacySendState('sending');
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, 3_000);
        });
        if (generation !== sendGeneration.current) return;
      }

      const result = await sendMessage({
        variables: {
          body: '',
          ccRecipients,
          notes: preserveLegacySemantics ? notes : normalizeValue(notes),
          participantId,
          preserveLegacySemantics,
          toRecipients,
          urgent,
        },
      });

      if (isPromotedPage && generation !== sendGeneration.current) return;
      const response = result.data?.medcallSendParticipantMessage;
      if (response?.responseCode !== 200 || (isPromotedPage && (result.errors?.length || response.responseID !== participantId))) {
        setLegacySendState('idle');
        toast.error(
          preserveLegacySemantics
            ? 'Error sending message'
            : isPromotedPage ? 'Unable to send the message.' : response?.message || 'Unable to send the message.',
        );
        return;
      }

      if (preserveLegacySemantics) {
        setLegacySendState('sent');
        window.setTimeout(() => {
          if (generation === sendGeneration.current) onOpenChange(false);
        }, 3_000);
        return;
      }

      toast.success('Message sent.');
      if (isPromotedPage) sendInFlight.current = false;
      handleDialogOpenChange(false);
    } catch (mutationError: unknown) {
      setLegacySendState('idle');
      toast.error(
        preserveLegacySemantics
          ? 'Error sending message'
          : isPromotedPage ? 'Unable to send the message.' : (mutationError as { message?: string } | null)?.message || 'Unable to send the message.',
      );
    } finally {
      if (isPromotedPage) {
        sendInFlight.current = false;
        setSubmitting(false);
      }
    }
  };

  if (preserveLegacySemantics && legacySendState !== 'idle') {
    return (
      <Dialog onOpenChange={handleDialogOpenChange} open={open}>
        <DialogContent
          aria-describedby={undefined}
          className="medcall2-message-dialog"
          contentHeight="auto"
          contentMaxWidth="calc(100vw - 32px)"
          contentTop="100px"
          contentWidth="748px"
          hideCloseButton
          onEscapeKeyDown={(event) => event.preventDefault()}
          overlayClassName="medcall2-message-overlay"
        >
          <DialogHeader className="medcall2-message-dialog__header"><DialogTitle>Send Message</DialogTitle></DialogHeader>
          <div className="flex min-h-64 items-center justify-center px-6 py-12 text-center" role="status">
            <div className="space-y-3">
              {legacySendState === 'sent' ? (
                <div aria-hidden="true" className="mx-auto text-4xl text-[var(--semantic-success)]">✓</div>
              ) : null}
              <div className="text-xl font-medium text-[var(--neutral-gray-120)]">
                {legacySendState === 'sending' ? 'Message is being sent...' : 'Message has been sent!'}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (preserveLegacySemantics) {
    const isErc = NURSELESS_PROGRAMS.includes(normalizeValue(participant.program));
    const isProject = participant.program === 'Project Sites';
    const legacyDob = formatDate(participant.dob).replace(/\/(\d{2})(\d{2})$/, '/$2');
    return (
      <Dialog onOpenChange={handleDialogOpenChange} open={open}>
        <DialogContent
          aria-describedby={undefined}
          className="medcall2-message-dialog"
          contentHeight="auto"
          contentMaxHeight="calc(100vh - 116px)"
          contentMaxWidth="calc(100vw - 32px)"
          contentTop="100px"
          contentWidth="748px"
          hideCloseButton
          onOpenAutoFocus={(event) => event.preventDefault()}
          overlayClassName="medcall2-message-overlay"
        >
          <DialogHeader className="medcall2-message-dialog__header"><DialogTitle>Send Message</DialogTitle></DialogHeader>
          <Medcall2MessageForm
            ccRecipients={ccRecipientsText}
            error={isPromotedPage ? defaultsError : error?.message}
            loading={loading}
            notes={notes}
            onCancel={() => handleDialogOpenChange(false)}
            onCcChange={setCcRecipientsText}
            onNotesChange={setNotes}
            onSubmit={handleSendMessage}
            onToChange={(value) => { setToRecipientsText(value); setLegacyToRequired(false); }}
            onUrgentChange={setUrgent}
            participantName={`${normalizeValue(participant.firstname)} ${normalizeValue(participant.lastname)}`}
            previewRows={[
              [
                { label: 'Program', value: normalizeValue(participant.program) },
                { label: 'DOB', value: legacyDob },
                { label: 'CID', value: normalizeValue(participant.cid) },
                { label: 'Provider', value: normalizeValue(participant.provider) },
              ],
              [
                { label: 'Facility', value: isErc ? 'N/A' : normalizeValue(participant.facility) },
                { label: 'psychiatrist', value: isErc || isProject ? 'N/A' : formatPersonLabel(participant.psychiatrist) },
                { label: 'Staff Assigned', value: normalizeValue(participant.staff_assigned) },
                { label: 'Nurse', value: isProject ? 'N/A' : formatPersonLabel(participant.nurse_name, participant.program) },
                { label: 'Status', value: participant.active ? 'Active' : 'Inactive' },
              ],
            ]}
            sendDisabled={isPromotedPage && defaultsUnavailable}
            sending={sendPending}
            toRecipients={toRecipientsText}
            toRequired={legacyToRequired}
            urgent={urgent}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog onOpenChange={handleDialogOpenChange} open={open}>
      <DialogContent className="sm:max-w-3xl">
        <form className="space-y-5" onSubmit={handleSendMessage}>
          <DialogHeader>
            <DialogTitle>Send Message</DialogTitle>
            <DialogDescription>Review recipients, add notes, and send the client message.</DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] px-4 py-3 text-sm text-[var(--neutral-gray-110)]">
              Loading message defaults...
            </div>
          ) : null}

          {error || defaultsError ? (
            <div className="rounded-[var(--border-radius-medium)] border border-[var(--semantic-error-border-soft)] bg-[var(--semantic-error-soft)] px-4 py-3 text-sm text-[var(--semantic-error)]">
              {isPromotedPage ? defaultsError : error?.message}
            </div>
          ) : null}

          {open && !loading && !error && draft && !draft.providerRecipientFound ? (
            <div className="rounded-[var(--border-radius-medium)] border border-[var(--semantic-warning-border-soft)] bg-[var(--semantic-warning-soft)] px-4 py-3 text-sm text-[var(--neutral-gray-120)]">
              No default recipients were found for this provider. Add the email addresses you want to use.
            </div>
          ) : null}

          <div className="grid gap-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--neutral-gray-120)]" htmlFor="medcall-message-to">
                {preserveLegacySemantics ? 'To:' : 'To'}
              </label>
              <Textarea
                disabled={fieldsPending}
                id="medcall-message-to"
                onChange={(event) => setToRecipientsText(event.target.value)}
                placeholder={preserveLegacySemantics ? 'email recipients' : 'Enter email addresses separated by commas'}
                rows={3}
                value={toRecipientsText}
              />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--neutral-gray-120)]" htmlFor="medcall-message-cc">
                {preserveLegacySemantics ? 'CC:' : 'CC'}
              </label>
              <Textarea
                disabled={fieldsPending}
                id="medcall-message-cc"
                onChange={(event) => setCcRecipientsText(event.target.value)}
                placeholder={preserveLegacySemantics ? 'email recipients' : 'Enter email addresses separated by commas'}
                rows={2}
                value={ccRecipientsText}
              />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--neutral-gray-120)]" htmlFor="medcall-message-subject">
                Subject
              </label>
              <Input
                disabled
                id="medcall-message-subject"
                value={subject || 'MedCall Message'}
              />
            </div>

            <div className="flex items-center gap-3 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] px-3 py-2">
              <Checkbox
                checked={urgent}
                disabled={fieldsPending}
                id="medcall-message-urgent"
                onCheckedChange={(checked) => setUrgent(checked === true)}
              />
              <label className="cursor-pointer text-sm font-medium text-[var(--neutral-gray-120)]" htmlFor="medcall-message-urgent">
                {preserveLegacySemantics ? 'Urgent' : 'Mark as urgent'}
              </label>
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--neutral-gray-120)]" htmlFor="medcall-message-notes">
                Notes
              </label>
              <Textarea
                disabled={fieldsPending}
                id="medcall-message-notes"
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Add message notes"
                rows={6}
                value={notes}
              />
            </div>
          </div>

          <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] p-4">
            <div className="mb-3 text-sm font-semibold text-[var(--neutral-gray-120)]">
              {preserveLegacySemantics ? 'Message Body' : 'Message Preview'}
            </div>
            <div className="grid gap-3 text-sm text-[var(--neutral-gray-110)] md:grid-cols-2">
              <div><span className="font-medium text-[var(--neutral-gray-120)]">Client:</span> {normalizeValue(participant.firstname)} {normalizeValue(participant.lastname)}</div>
              <div><span className="font-medium text-[var(--neutral-gray-120)]">Program:</span> {normalizeValue(participant.program) || '—'}</div>
              <div><span className="font-medium text-[var(--neutral-gray-120)]">Participant ID:</span> {participant.participant_id || '—'}</div>
              <div><span className="font-medium text-[var(--neutral-gray-120)]">DOB:</span> {formatDate(participant.dob)}</div>
              <div><span className="font-medium text-[var(--neutral-gray-120)]">CID:</span> {normalizeValue(participant.cid) || '—'}</div>
              <div><span className="font-medium text-[var(--neutral-gray-120)]">Provider:</span> {normalizeValue(participant.provider) || '—'}</div>
              <div><span className="font-medium text-[var(--neutral-gray-120)]">Facility:</span> {NURSELESS_PROGRAMS.includes(normalizeValue(participant.program)) ? 'N/A' : (normalizeValue(participant.facility) || '—')}</div>
              <div><span className="font-medium text-[var(--neutral-gray-120)]">Date Added:</span> {formatDateTime(participant.created_at)}</div>
              <div><span className="font-medium text-[var(--neutral-gray-120)]">Assigned Staff:</span> {normalizeValue(participant.staff_assigned) || '—'}</div>
              <div><span className="font-medium text-[var(--neutral-gray-120)]">Nurse:</span> {formatPersonLabel(participant.nurse_name, participant.program)}</div>
              <div><span className="font-medium text-[var(--neutral-gray-120)]">Psychiatrist:</span> {formatPersonLabel(participant.psychiatrist)}</div>
              <div><span className="font-medium text-[var(--neutral-gray-120)]">Status:</span> {participant.active ? 'Active' : 'Inactive'}</div>
            </div>

            {normalizeValue(notes) ? (
              <div className="mt-4 border-t border-[var(--neutral-gray-40)] pt-4 text-sm text-[var(--neutral-gray-110)]">
                <div className="mb-2 font-medium text-[var(--neutral-gray-120)]">Notes</div>
                <div className="whitespace-pre-wrap">{normalizeValue(notes)}</div>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button disabled={sendPending} onClick={() => handleDialogOpenChange(false)} type="button" variant="outline">
              Cancel
            </Button>
            <Button disabled={loading || sendPending || (isPromotedPage && defaultsUnavailable)} type="submit">
              {sendPending ? 'Sending...' : 'Send Message'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
